/*
 * ═══════════════════════════════════════════════════════════════════════
 * 🔒🔒🔒  ARCHIVO BLOQUEADO POR ORDEN DEL CREADOR  🔒🔒🔒
 * ═══════════════════════════════════════════════════════════════════════
 * NO MODIFICAR sin autorización explícita del usuario.
 * Para desbloquear, el usuario debe identificarse como creador
 * (ej. "yo soy el creador"). Ver: /app/memory/LOCKED_FILES.md
 * ═══════════════════════════════════════════════════════════════════════
 *
 * 🟦 CUADRO NEGRO — Sistema Completo de Proyectos del Builder.
 *
 * Panel "Mis Proyectos del Builder" accesible desde Lyon Genio. Es una vista
 * sincronizada con `/api/apps` (misma fuente de datos que Mis Apps), enfocada
 * en abrir/continuar rápido un proyecto desde dentro del chat.
 *
 * - Lista las apps del usuario con nombre, estado, slug y última URL.
 * - Click "Abrir en Builder" → window.location.href = `/apps/${id}/builder`
 *   (hard reload garantiza mount limpio).
 * - "Eliminar" usa confirm nativo + DELETE /api/apps/{id}.
 * - Para acciones avanzadas (publicar, duplicar, historial), envía al usuario
 *   a /my-apps que ya tiene esa UI completa — evita duplicar lógica.
 */
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, FolderOpen, RefreshCw, ExternalLink, Trash2, Plus } from 'lucide-react';
import { getToken } from '../utils/authToken';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 🟥 CUADRO NEGRO 2026-06-27 — fetchWithRetry401: misma estrategia que el
// MarketplacePanel para sobrevivir race-conditions de cookie httpOnly en
// Producción (el primer GET tras login a veces falla con 401 antes de que
// el cookie esté completamente propagado al SW/CDN). 4 intentos con
// backoff: 0/300/700/1500ms. Solo si LOS CUATRO fallan con 401 mostramos
// "Sesión expirada".
async function fetchWithRetry401(url, options = {}) {
  const token = getToken();
  const headers = { ...(options.headers || {}) };
  if (token && token !== 'cookie-session') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const backoffs = [0, 300, 700, 1500];
  let res = null;
  for (let i = 0; i < backoffs.length; i++) {
    if (backoffs[i] > 0) {
      await new Promise((r) => setTimeout(r, backoffs[i]));
    }
    res = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
    if (res.status !== 401) break;
  }
  return res;
}

export default function LyonProyectosPanel({ open, onClose, es = true }) {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // 🟥 CUADRO NEGRO 2026-06-27 — retry-on-401 con backoff exponencial.
      // En Producción el primer fetch post-login a veces falla con 401
      // (cookie httpOnly aún no propagada). 4 intentos antes de marcar
      // sesión como expirada.
      const r = await fetchWithRetry401(`${API}/apps`);
      if (r.status === 401) {
        setError(es ? 'Sesión expirada. Volvé a iniciar sesión.' : 'Session expired. Please log in again.');
        setApps([]);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setApps(Array.isArray(j.items) ? j.items : []);
    } catch (e) {
      console.error('LyonProyectos fetch error', e);
      setError(es ? 'No pudimos cargar tus proyectos.' : "We couldn't load your projects.");
    } finally {
      setLoading(false);
    }
  }, [es]);

  useEffect(() => {
    if (open) fetchApps();
  }, [open, fetchApps]);

  const handleOpen = () => {
    // 🟥 CUADRO NEGRO MAESTRO (sec 2) — este panel vive DENTRO del Genio:
    // no puede abrir el Builder. Lleva a la página dedicada, donde está el
    // único botón permitido ("Abrir Builder (modo visual)").
    window.location.href = '/builder-projects';
  };

  const handleDelete = async (app) => {
    const confirmMsg = es
      ? `¿Eliminar definitivamente "${app.name}" y todos sus archivos?`
      : `Permanently delete "${app.name}" and all its files?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await fetchWithRetry401(`${API}/apps/${app.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setApps((prev) => prev.filter((a) => a.id !== app.id));
    } catch (e) {
      console.error('Delete error', e);
      setError(es ? 'No pudimos eliminar el proyecto.' : "We couldn't delete the project.");
    }
  };

  const handleCreateNew = () => {
    // Reusa la UI completa de Mis Apps (modal de creación).
    window.location.href = '/my-apps';
  };

  if (!open) return null;

  // 🟥 CUADRO NEGRO DEFINITIVO 2026-06-27 — createPortal a document.body +
  // z-index 999999. Sin este wrap el panel queda detrás de la app shell
  // de DamyxIA (mismo bug que tenía el Marketplace).
  return createPortal((
    <div
      className="fixed inset-0 z-[999999] flex items-start justify-center pt-12 px-4 bg-black/60 backdrop-blur-sm"
      data-testid="lyon-proyectos-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 bg-slate-900/80">
          <div className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-cyan-300" />
            <h2 className="text-sm font-semibold text-white">
              {es ? 'Mis Proyectos del Builder' : 'My Builder Projects'}
            </h2>
            <span className="text-xs text-slate-400">({apps.length})</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={fetchApps}
              disabled={loading}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:opacity-50"
              data-testid="lyon-proyectos-refresh"
              aria-label={es ? 'Refrescar' : 'Refresh'}
              title={es ? 'Refrescar' : 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={handleCreateNew}
              className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium"
              data-testid="lyon-proyectos-new"
              title={es ? 'Crear nuevo proyecto' : 'Create new project'}
            >
              <Plus className="w-3.5 h-3.5" />
              {es ? 'Nuevo' : 'New'}
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="lyon-proyectos-close"
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
              aria-label={es ? 'Cerrar' : 'Close'}
              title={es ? 'Cerrar' : 'Close'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="max-h-[70vh] overflow-y-auto p-4">
          {error && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="lyon-proyectos-error">
              {error}
            </div>
          )}

          {!error && loading && apps.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm" data-testid="lyon-proyectos-loading">
              {es ? 'Cargando proyectos…' : 'Loading projects…'}
            </div>
          )}

          {!loading && !error && apps.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm" data-testid="lyon-proyectos-empty">
              {es
                ? 'Todavía no tenés proyectos. Creá uno desde Marketplace o "Crear nuevo".'
                : 'You have no projects yet. Create one from Marketplace or "Create new".'}
            </div>
          )}

          <ul className="space-y-2">
            {apps.map((app) => (
              <li
                key={app.id}
                data-testid={`lyon-proyecto-item-${app.id}`}
                className="flex items-center gap-3 p-3 rounded-xl bg-slate-800/50 border border-slate-700/60 hover:border-slate-600 transition-colors"
              >
                {/* 🟦 CUADRO NEGRO #1 FASE 3 — Thumbnail real con fallback. */}
                <div className="relative w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border border-slate-700/50 flex items-center justify-center shrink-0 overflow-hidden">
                  <FolderOpen className="w-5 h-5 text-indigo-400/60" />
                  <img
                    src={`${API}/apps/${app.id}/thumbnail`}
                    alt=""
                    loading="lazy"
                    crossOrigin="use-credentials"
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-white truncate">{app.name}</h3>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                        app.status === 'published'
                          ? 'bg-emerald-700/40 text-emerald-200 border border-emerald-600/40'
                          : 'bg-slate-700/60 text-slate-300 border border-slate-600/40'
                      }`}
                    >
                      {app.status === 'published' ? (es ? 'publicada' : 'published') : 'draft'}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 truncate font-mono">/u/{app.owner_slug}/{app.slug}/</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleOpen(app.id)}
                    data-testid={`lyon-proyecto-open-${app.id}`}
                    title={es ? 'Abrir en Builder' : 'Open in Builder'}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {es ? 'Abrir en Builder' : 'Open in Builder'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(app)}
                    data-testid={`lyon-proyecto-delete-${app.id}`}
                    title={es ? 'Eliminar' : 'Delete'}
                    className="p-1.5 rounded-lg text-red-300 hover:text-white hover:bg-red-700/40"
                    aria-label={es ? 'Eliminar' : 'Delete'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {apps.length > 0 && (
            <p className="mt-4 text-[11px] text-slate-500 text-center">
              {es
                ? 'Para acciones avanzadas (publicar, duplicar, historial), abrí "Mis Apps" desde el menú.'
                : 'For advanced actions (publish, duplicate, history), open "My Apps" from the menu.'}
            </p>
          )}
        </div>
      </div>
    </div>
  ), document.body);
}
